import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface State {
  /** Post URIs returned missing by the API check (deleted/private on Bluesky). */
  missingUris: string[]
  /** Unix ms of the last *completed* check. null = never completed. */
  lastCheckedAt: number | null
  /** How many posts were checked in the last completed run. */
  totalChecked: number
  /** How far through db.posts the last run got (sorted-URI index).
   *  0 = either never started or fully completed.
   *  >0 = interrupted — use as startFromOffset to resume. */
  checkedOffset: number
  /** Total posts in db.posts at the time the current/last run started. */
  totalPosts: number
  /** True while a check is actively running. Transient — not persisted. */
  isChecking: boolean

  /** Reset missingUris + offset before a brand-new check. */
  startFresh: () => void
  /** Stream one batch's newly-found missing URIs into the store. */
  addMissing: (uris: string[], offset: number, total: number) => void
  /** Mark a run as fully complete. */
  finalize: (total: number) => void
  /** Toggle the running flag (and set totalPosts on start). */
  setIsChecking: (v: boolean, totalPosts?: number) => void
  /** Update progress offset every batch (even when no new deletions found). */
  setProgress: (offset: number, total: number) => void

  /** Merge URIs confirmed deleted OUTSIDE a full scan (e.g. a gallery
   *  tile 404'd and the post no longer resolves). Leaves the scan's
   *  offset/progress state untouched. */
  markMissing: (uris: string[]) => void

  /** Full-replace result (used by SyncCheckSection + Settings after a
   *  non-streaming run, or after a fresh run that was not streamed). */
  setResult: (missing: string[], total: number) => void
  clear: () => void
}

export const useDeletionCheck = create<State>()(
  persist(
    (set, get) => ({
      missingUris: [],
      lastCheckedAt: null,
      totalChecked: 0,
      checkedOffset: 0,
      totalPosts: 0,
      isChecking: false,

      startFresh: () => set({ missingUris: [], checkedOffset: 0, totalPosts: 0 }),

      addMissing: (uris, offset, total) =>
        set((s) => ({
          missingUris: uris.length
            ? [...new Set([...s.missingUris, ...uris])]
            : s.missingUris,
          checkedOffset: offset,
          totalPosts: total,
        })),

      finalize: (total) =>
        set({
          lastCheckedAt: Date.now(),
          totalChecked: total,
          checkedOffset: 0,
          totalPosts: total,
        }),

      setIsChecking: (v, totalPosts) =>
        set(v && totalPosts != null ? { isChecking: true, totalPosts } : { isChecking: v }),

      setProgress: (offset, total) => set({ checkedOffset: offset, totalPosts: total }),

      markMissing: (uris) =>
        set((s) => ({
          missingUris: [...new Set([...s.missingUris, ...uris])],
        })),

      setResult: (missing, total) =>
        set({
          missingUris: missing,
          lastCheckedAt: Date.now(),
          totalChecked: total,
          checkedOffset: 0,
          totalPosts: total,
          isChecking: false,
        }),

      clear: () =>
        set({
          missingUris: [],
          lastCheckedAt: null,
          totalChecked: 0,
          checkedOffset: 0,
          totalPosts: 0,
          isChecking: false,
        }),

      // Suppress stale isChecking on hydration (process may have crashed).
      // Achieved via partialize below.
    }),
    {
      name: 'bskyhq:deletion-check',
      // isChecking is transient — never persist it.
      partialize: ({ isChecking: _ic, startFresh: _sf, addMissing: _am,
        finalize: _fn, setIsChecking: _si, setResult: _sr, clear: _cl,
        setProgress: _sp, markMissing: _mm,
        ...rest }) => rest,
    },
  ),
)
